/**
 * Garnkit — storefront configurator (view layer).
 * theme/assets/kit-builder.js
 *
 * ES module. No framework, no libraries. Ships alongside `kit-state.js` in
 * theme/assets/, which owns every derived value:
 *
 *     <script type="module" src="{{ 'kit-builder.js' | asset_url }}"></script>
 *
 * The relative import below resolves against this file's own URL, i.e.
 * `…/assets/kit-state.js`, which Shopify serves without the `?v=` cache-buster.
 *
 * ── Boot contract ─────────────────────────────────────────────────────────────
 * The section renders a JSON blob and a mount point:
 *
 *   <div class="kit-builder" data-kit-uid="{{ section.id }}">
 *     <div data-kit-mount></div>            (optional — falls back to the root)
 *     …static Liquid, e.g. the pickup block, may live outside the mount…
 *   </div>
 *   <script type="application/json" id="KitBuilderData">{ … }</script>
 *
 * Blob shape (every field is read defensively; unknown fields are ignored):
 *
 *   {
 *     "recipe":  { version, sizes:[{code,chest}], slots:[{key,label,handle,material,skeins{}}],
 *                  pattern:{label,included,variantId?}, fromPrice },
 *     "catalog": { "<slotKey>": { handle, title, price, featured_image,
 *                                 variants:[ /products/{handle}.js variant objects,
 *                                            each with Liquid's inventory_quantity
 *                                            and inventory_management merged in ] } },
 *     "settings": { showSkeins:true, allowIncomplete:false },
 *     "kitHandle": "…", "kitTitle": "…",
 *     "routes": { root_url, cart_add_url, cart_url, cart_change_url, checkout_url }
 *   }
 *
 * Counts come from Liquid (`/products/{handle}.js` does NOT expose
 * `inventory_quantity`); freshness comes from a `/products/{handle}.js` refresh on
 * load, cached in sessionStorage. A failed refresh degrades silently to the Liquid
 * data — it can never break the page. `/cart/add.js` stays the final authority on
 * oversell.
 *
 * ── State ────────────────────────────────────────────────────────────────────
 *   state = { size: null|'<sizeCode>', colors: { <slotKey>: <variantId>|null },
 *             expanded: null|'<slotKey>' }
 *
 * That object is the whole configuration. Prices, totals, skein counts, swatch
 * flags and the add-to-cart reason are computed by kit-state.js on every render
 * and are NEVER written back onto it. Transient view flags (busy, server error,
 * size-change notice) live in a separate `ui` object so the state shape stays
 * exactly what kit-state.js expects.
 *
 * Colour choices are never silently cleared. A size change that invalidates a
 * preserved colour is explained, not undone.
 */

import * as KitState from './kit-state.js';

/* ══════════════════════════════════════════════════════════════════════════ *
 * Constants and Danish copy
 * ══════════════════════════════════════════════════════════════════════════ */

/** Availability refresh cache. Short by design — stock moves. */
const CACHE_PREFIX = 'garnkit:p:';
const CACHE_TTL_MS = 120000;

/** The one breakpoint kit-builder.css uses. */
const MOBILE_QUERY = '(max-width: 768px)';

/**
 * All customer-facing copy, in one place.
 *
 * Strings marked VERBATIM are taken word for word from docs/BUILD-CONTEXT.md.
 * Where the CSS applies `text-transform: uppercase` the DOM keeps sentence case
 * (STØRRELSE renders from "Størrelse"), so assistive tech reads normal Danish
 * while the page shows the design's caps.
 */
const COPY = {
  /* Steps */
  sizeLabel: 'Størrelse',                                   // VERBATIM `STØRRELSE`
  sizeUnset: 'ikke valgt',
  yarnLabel: 'Garn',                                        // VERBATIM `GARN`
  yarnLocked: 'låst indtil størrelse er valgt',
  sizeSummary: (code, chest) =>
    chest ? `— str. ${code}, brystvidde ${chest}` : `— str. ${code}`,
  yarnSummary: (done, all) => `— ${done} af ${all} valgt`,

  /* Slot header */
  unitPrice: (price) => `${price} / nøgle`,
  skeins: (n) => `Antal nøgler: ${n}`,                      // VERBATIM `Antal nøgler: 7`
  skeinsNone: 'Antal nøgler: —',                            // VERBATIM
  statusLocked: 'Vælg størrelse først',                     // VERBATIM
  statusMissing: 'Mangler farve',                           // VERBATIM
  statusShort: 'For lidt på lager',                         // VERBATIM
  statusReady: 'Klar',                                      // VERBATIM

  /* Collapsed row */
  changeLong: 'Skift farve',                                // VERBATIM `SKIFT FARVE`
  changeShort: 'Skift',                                     // VERBATIM `SKIFT`
  // FLAGGED ADDITION — the open control for a slot that has no colour yet.
  // Reuses the wording of the approved reason string "Vælg farve for hver
  // garnkvalitet"; without it a closed, colourless slot has no way to be opened.
  pickLong: 'Vælg farve',
  pickShort: 'Vælg',
  chosenSub: (qty, unit, line) => `${qty} nøgler × ${unit} = ${line}`,
  colourCount: (n) => `${n} farver`,

  /* Palette */
  paletteLabel: 'Farve',                                    // VERBATIM `FARVE — 13 farver`
  paletteCount: (n) => `— ${n} farver`,
  collapseLong: 'Fold sammen',                              // VERBATIM
  collapseShort: 'Luk',
  paletteHint: (n) => `Rul i paletten · ${n} farver · kun ét garn er åbent ad gangen`,

  /* Swatches */
  soldOut: 'Udsolgt',                                       // VERBATIM `UDSOLGT`
  onlyN: (n) => `Kun ${n}`,                                 // VERBATIM `KUN 3`
  requires: (n) => `kræver ${n}`,                           // VERBATIM `kræver 7`

  /* Alerts and banners */
  shortLead: (stock, size, need) => `Kun ${stock} på lager — str. ${size} kræver ${need}.`,
  shortRest: ' Din farve er bevaret; vælg en anden farve eller en mindre størrelse.',
  // FLAGGED ADDITION — a chosen colour that has gone sold out (deep link, or the
  // load-time refresh overtaking the Liquid render). Mirrors the approved
  // "<quality> er udsolgt i alle farver." sentence at colour level.
  soldOutLead: (colour) => `${colour} er udsolgt.`,
  soldOutRest: ' Vælg en anden farve.',
  qualityGoneLead: (label) => `${label} er udsolgt i alle farver.`,
  qualityGoneRest: ' Kittet kan ikke købes lige nu. ',
  qualityGoneLink: 'Få besked når det er tilbage',          // VERBATIM

  /* Price panel */
  priceEmpty: 'Vælg farver for at se samlet pris',          // VERBATIM
  totalLabel: 'Samlet pris',                                // VERBATIM `SAMLET PRIS`
  lineLabel: (label, colour, qty, unit) => `${label} · ${colour} · ${qty} × ${unit}`,
  patternLine: (label) => label,
  patternFree: 'Gratis',

  /* Stock line and buttons */
  stockOk: 'På lager, klar til afsendelse',                 // VERBATIM
  stockShort: 'Ikke nok på lager til den valgte størrelse',
  atc: 'Tilføj kit til kurv',                               // VERBATIM `TILFØJ KIT TIL KURV`
  buyNow: 'Køb nu',                                         // VERBATIM `KØB NU`
  // FLAGGED ADDITION — only shown when /cart/add.js fails without a message of
  // its own. Shopify's own Danish error text is preferred whenever it is present.
  cartError: 'Kittet kunne ikke lægges i kurven. Prøv igen.',

  /* Optional recipe opt-out */
  patternOptOut: 'Jeg har allerede opskriften',

  /* Screen-reader only */
  srTotal: (total) => `Samlet pris ${total}`
};

/* ══════════════════════════════════════════════════════════════════════════ *
 * Small utilities
 * ══════════════════════════════════════════════════════════════════════════ */

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/** Escapes text and attribute values. Every interpolation below goes through it. */
function esc(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

/** Keeps generated ids and radio-group names valid whatever the section id is. */
function slug(value) {
  return String(value == null ? '' : value).replace(/[^A-Za-z0-9_-]/g, '-');
}

/** Splits "Lead sentence. Rest of it." into its bold lead and the remainder. */
function splitLead(text) {
  const s = String(text || '');
  const at = s.indexOf('. ');
  if (at === -1) return { lead: s, rest: '' };
  return { lead: s.slice(0, at + 1), rest: s.slice(at + 1) };
}

/** RFC-4122-ish id for `_kit_id`. Falls back when crypto.randomUUID is absent. */
function uuid() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const b = crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const hex = Array.from(b, (n) => n.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch (err) {
    /* fall through */
  }
  return `kit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** sessionStorage that never throws (Safari private mode, quota, disabled storage). */
const store = {
  get(key) {
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      /* cache is an optimisation, never a requirement */
    }
  }
};

/** Joins a shop root ("/", "/da-dk") with a path, without doubling slashes. */
function joinPath(root, path) {
  const base = String(root || '/').replace(/\/+$/, '');
  return `${base}${path}`;
}

/* ══════════════════════════════════════════════════════════════════════════ *
 * The configurator
 * ══════════════════════════════════════════════════════════════════════════ */

let instanceCounter = 0;

class KitBuilder {
  /**
   * @param {HTMLElement} root  the `.kit-builder` element
   * @param {object}      data  the parsed KitBuilderData blob
   */
  constructor(root, data) {
    this.root = root;
    this.mount = root.querySelector('[data-kit-mount]') || root;

    this.recipe = (data && data.recipe) || { sizes: [], slots: [] };
    // Merge boot-resolved pattern data (variantId + live price) into the recipe
    // so cartPayload() can add the physical recipe product to the cart.
    if (data && data.pattern && data.pattern.variantId != null) {
      const bp = data.pattern;
      const rp = this.recipe.pattern || {};
      this.recipe = {
        ...this.recipe,
        pattern: {
          ...rp,
          variantId: bp.variantId,
          livePrice: Number.isFinite(bp.price) ? bp.price : 0
        }
      };
    }
    this.catalog = this.cloneCatalog((data && data.catalog) || {});
    this.settings = this.readSettings(data && data.settings);
    this.routes = this.readRoutes(data && data.routes);
    this.kitHandle = (data && data.kitHandle) || '';
    this.kitTitle = (data && data.kitTitle) || document.title || '';

    this.uid = slug(root.dataset.kitUid || root.id || `kit-${++instanceCounter}`);

    /** Whether the customer has opted out of the optional recipe product. */
    this._patternOptedOut = false;

    /** The canonical configuration. Nothing derived ever lands here. */
    this.state = { size: null, colors: {}, expanded: null };
    this.slots().forEach((slot) => {
      this.state.colors[slot.key] = null;
    });

    /** Transient view flags — deliberately NOT part of `state`. */
    this.ui = {
      notice: null,          // "Du skiftede til str. XL …"
      serverError: null,     // message from /cart/add.js
      busy: false,           // an add is in flight
      suppressAuto: false,   // the shopper collapsed the auto-opened slot
      pointerSelect: false   // the pending colour change came from a pointer
    };

    /** Bumped whenever the catalog is replaced, so render signatures invalidate. */
    this.catalogRev = 0;
    /** Region signatures, for skipping untouched DOM. */
    this.sig = { size: null, yarnHead: null, slots: {}, summary: null };
    /** Suppresses the aria-live region on first paint. */
    this.announced = false;
    this.lastAnnouncement = '';

    this.handleClick = this.handleClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerdown = this.handlePointerdown.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);

    this.mobile = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
    this.isMobile = this.mobile ? this.mobile.matches : false;
  }

  /* ---------------------------------------------------------------------- *
   * Boot
   * ---------------------------------------------------------------------- */

  init() {
    this.restoreFromUrl();
    this.buildShell();
    this.bind();
    this.render();
    // Availability refresh is fire-and-forget: the page is already usable.
    this.refreshAvailability();
  }

  destroy() {
    this.root.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('change', this.handleChange);
    this.root.removeEventListener('keydown', this.handleKeydown, true);
    this.root.removeEventListener('pointerdown', this.handlePointerdown, true);
    if (this.mobile) {
      if (this.mobile.removeEventListener) this.mobile.removeEventListener('change', this.handleMediaChange);
      else if (this.mobile.removeListener) this.mobile.removeListener(this.handleMediaChange);
    }
  }

  bind() {
    this.root.addEventListener('click', this.handleClick);
    this.root.addEventListener('change', this.handleChange);
    // Capture phase: the keyboard flag must be set before the click it produces.
    this.root.addEventListener('keydown', this.handleKeydown, true);
    this.root.addEventListener('pointerdown', this.handlePointerdown, true);
    if (this.mobile) {
      if (this.mobile.addEventListener) this.mobile.addEventListener('change', this.handleMediaChange);
      else if (this.mobile.addListener) this.mobile.addListener(this.handleMediaChange);
    }
  }

  /** Static skeleton: the regions render into these, the live region persists. */
  buildShell() {
    this.mount.innerHTML =
      `<div class="kit-builder__live" role="status" aria-live="polite" aria-atomic="true"></div>` +
      `<hr class="kit-builder__divider">` +
      `<div data-kit-region="size"></div>` +
      `<div class="kit-builder__step" data-kit-region="yarn">` +
        `<div class="kit-builder__step-head" data-kit-region="yarn-head"></div>` +
        `<div class="kit-slots" data-kit-region="slots"></div>` +
      `</div>` +
      `<div data-kit-region="summary"></div>`;

    this.nodes = {
      live: this.mount.querySelector('.kit-builder__live'),
      size: this.mount.querySelector('[data-kit-region="size"]'),
      yarnHead: this.mount.querySelector('[data-kit-region="yarn-head"]'),
      slots: this.mount.querySelector('[data-kit-region="slots"]'),
      summary: this.mount.querySelector('[data-kit-region="summary"]')
    };

    // One wrapper per slot, created once and held by reference (no selector
    // round-trip, so any slot key is safe). The quality-unavailable banner lives
    // inside the wrapper but outside the card, as the design requires.
    this.slotNodes = {};
    this.slots().forEach((slot) => {
      const node = document.createElement('div');
      node.setAttribute('data-kit-slot', slot.key);
      this.nodes.slots.appendChild(node);
      this.slotNodes[slot.key] = node;
    });
  }

  /* ---------------------------------------------------------------------- *
   * Input normalisation
   * ---------------------------------------------------------------------- */

  slots() {
    return Array.isArray(this.recipe && this.recipe.slots) ? this.recipe.slots : [];
  }

  sizes() {
    return Array.isArray(this.recipe && this.recipe.sizes) ? this.recipe.sizes : [];
  }

  /** Deep-enough copy so the availability refresh never mutates the boot blob. */
  cloneCatalog(catalog) {
    const out = {};
    Object.keys(catalog || {}).forEach((key) => {
      const entry = catalog[key] || {};
      out[key] = {
        handle: entry.handle || '',
        title: entry.title || '',
        price: Number.isFinite(entry.price) ? entry.price : null,
        featured_image: entry.featured_image || null,
        variants: Array.isArray(entry.variants) ? entry.variants.map((v) => Object.assign({}, v)) : []
      };
    });
    return out;
  }

  /** Merchant settings, tolerant of camelCase and snake_case keys. */
  readSettings(settings) {
    const s = settings || {};
    const pick = (...keys) => {
      for (const key of keys) if (s[key] !== undefined && s[key] !== null) return s[key];
      return undefined;
    };
    const showSkeins = pick('showSkeins', 'show_skeins', 'showSkeinCount');
    const allowIncomplete = pick('allowIncomplete', 'allow_incomplete', 'allowIncompleteKits');
    const threshold = pick('lowStockThreshold', 'low_stock_threshold');
    const thresholdNum = Number(threshold);
    return {
      showSkeins: showSkeins === undefined ? true : showSkeins !== false,
      // Defaults off, per the product decision. When on it only relaxes the
      // stock gate — a kit with no size or a missing colour still cannot be
      // added, because there would be no line items to post.
      allowIncomplete: allowIncomplete === true,
      // A merchant early-warning floor (annotateVariants' `earlyWarning`).
      // Never affects `status`/checkout — see kit-state.js for why.
      lowStockThreshold: Number.isFinite(thresholdNum) && thresholdNum > 0 ? thresholdNum : null
    };
  }

  /**
   * Liquid-injected routes first (market/locale subpaths), then the theme's own
   * `window.theme.routes` (Archetype/Impulse), then the Shopify defaults.
   */
  readRoutes(routes) {
    const r = routes || {};
    const themeRoutes = (window.theme && window.theme.routes) || {};
    const root = r.root_url || r.rootUrl || themeRoutes.home || '/';
    return {
      root,
      cartAdd: r.cart_add_url || r.cartAdd || themeRoutes.cartAdd || joinPath(root, '/cart/add.js'),
      cartJs: r.cart_js_url || themeRoutes.cart || joinPath(root, '/cart.js'),
      cartUpdate: joinPath(root, '/cart/update.js'),
      cart: r.cart_url || r.cartUrl || themeRoutes.cartPage || joinPath(root, '/cart'),
      checkout: r.checkout_url || r.checkoutUrl || joinPath(root, '/checkout')
    };
  }

  /* ---------------------------------------------------------------------- *
   * Derived view model — every value recomputed, nothing stored
   * ---------------------------------------------------------------------- */

  /**
   * Which slot is open. Exactly one at most.
   *
   * `state.expanded` is authoritative. When nothing is explicitly open, the first
   * slot still missing a colour opens itself, so the flow guides the shopper
   * down the card stack — unless they closed it by hand ("Fold sammen").
   */
  expandedKey(views) {
    if (this.state.size === null) return null;
    if (this.state.expanded) return this.state.expanded;
    if (this.ui.suppressAuto) return null;
    const missing = views.find((view) => view.status === 'missing_colour');
    return missing ? missing.key : null;
  }

  /** slotView() for every slot, in recipe order. */
  slotViews() {
    return this.slots()
      .map((slot) =>
        KitState.slotView(this.state, this.recipe, this.catalog, slot.key, this.settings.lowStockThreshold)
      )
      .filter(Boolean);
  }

  /** The add-to-cart gate, after the "allow incomplete kits" setting is applied. */
  atcState() {
    const base = KitState.addToCartState(this.state, this.recipe, this.catalog);
    if (!base.enabled && base.reasonKind === 'insufficient' && this.settings.allowIncomplete) {
      // The shopper may proceed; /cart/add.js remains the final authority and
      // will reject a genuine oversell. The reason line still explains the risk.
      return { enabled: true, reasonKind: 'insufficient', reason: base.reason };
    }
    return base;
  }

  /* ---------------------------------------------------------------------- *
   * Render
   * ---------------------------------------------------------------------- */

  render() {
    const views = this.slotViews();
    const expanded = this.expandedKey(views);
    const atc = this.atcState();

    // A preserved-colour notice only stands while the shortage stands.
    if (this.ui.notice && atc.reasonKind !== 'insufficient') this.ui.notice = null;

    const focusKey = this.captureFocus();
    const scrolls = this.captureScroll();

    this.renderSize();
    this.renderYarnHead(views);
    views.forEach((view) => this.renderSlot(view, expanded === view.key));
    this.renderSummary(views, atc);

    this.restoreScroll(scrolls);
    this.restoreFocus(focusKey);
    this.announce(atc);
  }

  /* --- step 1: size ----------------------------------------------------- */

  renderSize() {
    const size = this.state.size;
    const all = this.sizes();
    const chosen = all.find((s) => s && s.code === size) || null;
    const signature = JSON.stringify([size, this.ui.notice]);
    if (this.sig.size === signature) return;
    this.sig.size = signature;

    const note = chosen
      ? COPY.sizeSummary(chosen.code, chosen.chest)
      : `— ${COPY.sizeUnset}`;

    // Roving tabindex: the selected size is the group's single tab stop, or the
    // first one while nothing is selected.
    const tabStop = chosen ? chosen.code : (all[0] && all[0].code);
    const buttons = all.map((s) => {
      const on = s.code === size;
      return (
        `<button type="button" class="kit-size${on ? ' is-selected' : ''}" role="radio"` +
        ` aria-checked="${on ? 'true' : 'false'}" tabindex="${s.code === tabStop ? '0' : '-1'}"` +
        ` data-kit-action="size" data-kit-value="${esc(s.code)}" data-fk="size:${esc(s.code)}">` +
          `<span class="kit-size__code">${esc(s.code)}</span>` +
          (s.chest ? `<span class="kit-size__chest">${esc(s.chest)}</span>` : '') +
        `</button>`
      );
    }).join('');

    let notice = '';
    if (this.ui.notice) {
      const parts = splitLead(this.ui.notice);
      notice =
        `<div class="kit-banner kit-banner--critical" role="status">` +
          `<span class="kit-banner__lead">${esc(parts.lead)}</span>${esc(parts.rest)}` +
        `</div>`;
    }

    this.nodes.size.className = 'kit-builder__step';
    this.nodes.size.innerHTML =
      `<div class="kit-builder__step-head">` +
        `<span class="kit-builder__label" id="${this.uid}-size-label">${esc(COPY.sizeLabel)}</span>` +
        `<span class="kit-builder__label-note">${esc(note)}</span>` +
      `</div>` +
      `<div class="kit-sizes" role="radiogroup" aria-labelledby="${this.uid}-size-label">${buttons}</div>` +
      notice;
  }

  /* --- step 2 head ------------------------------------------------------ */

  renderYarnHead(views) {
    const sized = this.state.size !== null;
    const done = views.filter((v) => v.chosenVariant).length;
    const signature = JSON.stringify([sized, done, views.length]);
    if (this.sig.yarnHead === signature) return;
    this.sig.yarnHead = signature;

    const note = sized ? COPY.yarnSummary(done, views.length) : `— ${COPY.yarnLocked}`;
    this.nodes.yarnHead.innerHTML =
      `<span class="kit-builder__label" id="${this.uid}-yarn-label">${esc(COPY.yarnLabel)}</span>` +
      `<span class="kit-builder__label-note">${esc(note)}</span>`;
  }

  /* --- one yarn slot ---------------------------------------------------- */

  renderSlot(view, isExpanded) {
    const signature = JSON.stringify([
      this.state.size, view.chosen, isExpanded, this.catalogRev,
      this.settings.showSkeins, this.isMobile
    ]);
    if (this.sig.slots[view.key] === signature) return;
    this.sig.slots[view.key] = signature;

    const slot = this.slots().find((s) => s.key === view.key) || {};
    const entry = this.catalog[view.key] || {};
    const sized = this.state.size !== null;
    const locked = view.status === 'locked';
    const error = view.status === 'insufficient';

    const cardClass =
      'kit-slot ' + (locked ? 'is-locked' : error ? 'is-error' : 'is-active');

    let html = `<div class="${cardClass}">${this.slotHead(view, slot, entry, sized)}`;
    if (!locked) {
      if (isExpanded) html += this.slotPalette(view);
      else html += this.slotChosenRow(view, isExpanded);
      html += this.slotAlert(view);
    }
    html += `</div>`;
    html += this.qualityBanner(view, slot);

    this.slotNodes[view.key].innerHTML = html;
  }

  slotHead(view, slot, entry, sized) {
    const thumb = entry.featured_image || (entry.variants[0] && entry.variants[0].featured_image
      ? entry.variants[0].featured_image.src : null);

    const statusText =
      view.status === 'locked' ? COPY.statusLocked
        : view.status === 'missing_colour' ? COPY.statusMissing
          : view.status === 'insufficient' ? COPY.statusShort
            : COPY.statusReady;
    const statusWarn = view.status === 'missing_colour' || view.status === 'insufficient';

    const skeins = this.settings.showSkeins
      ? `<span class="kit-slot__skeins${sized ? ' is-sized' : ''}">` +
        `${esc(view.required === null ? COPY.skeinsNone : COPY.skeins(view.required))}</span>`
      : '';

    return (
      `<div class="kit-slot__head">` +
        (thumb ? `<img class="kit-slot__thumb" src="${esc(thumb)}" alt="" loading="lazy" decoding="async">` : '') +
        `<div class="kit-slot__titles">` +
          `<div class="kit-slot__title-row">` +
            `<span class="kit-slot__name">${esc(view.label)}</span>` +
            `<span class="kit-slot__unit-price">${esc(COPY.unitPrice(KitState.formatDKK(view.unitPrice)))}</span>` +
          `</div>` +
          (slot.material ? `<div class="kit-slot__material">${esc(slot.material)}</div>` : '') +
          `<div class="kit-slot__meta">` +
            skeins +
            `<span class="kit-slot__status${statusWarn ? ' is-warning' : ''}">${esc(statusText)}</span>` +
          `</div>` +
        `</div>` +
      `</div>`
    );
  }

  /**
   * The collapsed row. With a colour it shows it plus SKIFT FARVE; without one it
   * is the way back into a palette that is currently closed.
   */
  slotChosenRow(view) {
    const chosen = view.chosenVariant;
    const count = view.variants.length;

    let swatch = '';
    let name;
    let sub;
    let longLabel;
    let shortLabel;

    if (chosen) {
      const colour = [chosen.code, chosen.name].filter(Boolean).join(' ');
      swatch = chosen.image
        ? `<img class="kit-slot__chosen-swatch" src="${esc(chosen.image)}" alt="" loading="lazy" decoding="async">`
        : '';
      name = colour;
      const effectivePrice = Number.isFinite(chosen.price) ? chosen.price : view.unitPrice;
      sub = view.required === null
        ? COPY.colourCount(count)
        : COPY.chosenSub(view.required, KitState.formatDKK(effectivePrice),
          KitState.formatDKK(view.required * effectivePrice));
      longLabel = COPY.changeLong;
      shortLabel = COPY.changeShort;
    } else {
      name = COPY.colourCount(count);
      sub = '';
      longLabel = COPY.pickLong;
      shortLabel = COPY.pickShort;
    }

    return (
      `<button type="button" class="kit-slot__chosen" aria-expanded="false"` +
      ` data-kit-action="expand" data-kit-slot-key="${esc(view.key)}" data-fk="chosen:${esc(view.key)}">` +
        swatch +
        `<span class="kit-slot__chosen-text">` +
          `<span class="kit-slot__chosen-name">${esc(name)}</span>` +
          (sub ? `<span class="kit-slot__chosen-sub">${esc(sub)}</span>` : '') +
        `</span>` +
        `<span class="kit-slot__change">` +
          `<span class="kit-slot__change-long">${esc(longLabel)}</span>` +
          `<span class="kit-slot__change-short">${esc(shortLabel)}</span>` +
        `</span>` +
      `</button>`
    );
  }

  /** The expanded palette: head, the swatch grid, and the mobile scroll hint. */
  slotPalette(view) {
    const paletteId = `${this.uid}-palette-${slug(view.key)}`;
    const labelId = `${this.uid}-pal-label-${slug(view.key)}`;
    const groupName = `${this.uid}-colour-${slug(view.key)}`;

    const swatches = view.variants.map((variant) => this.swatch(view, variant, groupName)).join('');

    return (
      `<div class="kit-slot__palette" id="${paletteId}" role="group" aria-labelledby="${labelId}">` +
        `<div class="kit-palette__head">` +
          `<span class="kit-palette__label" id="${labelId}">${esc(COPY.paletteLabel)} ` +
            `<span class="kit-palette__count">${esc(COPY.paletteCount(view.variants.length))}</span>` +
          `</span>` +
          `<button type="button" class="kit-palette__toggle" aria-expanded="true" aria-controls="${paletteId}"` +
          ` data-kit-action="collapse" data-kit-slot-key="${esc(view.key)}" data-fk="toggle:${esc(view.key)}">` +
            `<span class="kit-palette__toggle-long">${esc(COPY.collapseLong)}</span>` +
            `<span class="kit-palette__toggle-short">${esc(COPY.collapseShort)}</span>` +
          `</button>` +
        `</div>` +
        `<ul class="kit-swatches" data-kit-palette="${esc(view.key)}">${swatches}</ul>` +
        (this.isMobile
          ? `<p class="kit-palette__hint">${esc(COPY.paletteHint(view.variants.length))}</p>`
          : '') +
      `</div>`
    );
  }

  /**
   * One swatch: a real radio immediately followed by its label, exactly as the
   * stylesheet's `:checked + .kit-swatch__label` contract requires. Sold-out
   * radios are disabled, so they are inert for pointer and keyboard alike.
   */
  swatch(view, variant, groupName) {
    const id = `${this.uid}-sw-${slug(view.key)}-${slug(variant.id)}`;
    const soldOut = variant.status === 'sold_out';
    const low = variant.status === 'low';
    // earlyWarning is a merchant restock reminder (Settings › lagergrænse) — it
    // shares the low-stock plate visually, but NEVER the "kræver N" note below,
    // because the colour genuinely covers this order; only `low` means it doesn't.
    const warn = !soldOut && !low && variant.earlyWarning === true;
    const checked = view.chosen === variant.id;

    const classes =
      'kit-swatch' +
      (soldOut ? ' kit-swatch--soldout' : '') +
      (low || warn ? ' kit-swatch--low' : '');

    const plate = soldOut
      ? `<span class="kit-swatch__plate">${esc(COPY.soldOut)}</span>`
      : low || warn
        ? `<span class="kit-swatch__plate kit-swatch__plate--low">${esc(COPY.onlyN(variant.stock))}</span>`
        : '';

    const note = low && view.required !== null
      ? `<span class="kit-swatch__note">${esc(COPY.requires(view.required))}</span>`
      : '';

    return (
      `<li class="${classes}">` +
        `<input class="kit-swatch__input" type="radio" id="${id}" name="${groupName}"` +
        ` value="${esc(variant.id)}" data-kit-action="colour" data-kit-slot-key="${esc(view.key)}"` +
        ` data-fk="swatch:${esc(view.key)}:${esc(variant.id)}"` +
        `${checked ? ' checked' : ''}${soldOut ? ' disabled' : ''}>` +
        `<label class="kit-swatch__label" for="${id}">` +
          `<span class="kit-swatch__circle">` +
            (variant.image
              ? `<img class="kit-swatch__img" src="${esc(variant.image)}" alt="" loading="lazy" decoding="async">`
              : '') +
            plate +
          `</span>` +
          `<span class="kit-swatch__code">${esc(variant.code || '')}</span>` +
          `<span class="kit-swatch__name">${esc(variant.name || '')}</span>` +
          note +
        `</label>` +
      `</li>`
    );
  }

  /** Red strip across the bottom of a slot whose chosen colour cannot be bought. */
  slotAlert(view) {
    if (view.status !== 'insufficient' || !view.chosenVariant) return '';
    const chosen = view.chosenVariant;
    const colour = [chosen.code, chosen.name].filter(Boolean).join(' ');

    let lead;
    let rest;
    if (chosen.status === 'low' && chosen.stock !== null && view.required !== null) {
      lead = COPY.shortLead(chosen.stock, this.state.size, view.required);
      rest = COPY.shortRest;
    } else {
      // Sold out, or short with an unsubstantiated count — never print a number
      // we cannot stand behind.
      lead = COPY.soldOutLead(colour);
      rest = COPY.soldOutRest;
    }

    return (
      `<div class="kit-slot__alert" role="status">` +
        `<span class="kit-slot__alert-lead">${esc(lead)}</span>${esc(rest)}` +
      `</div>`
    );
  }

  /** Neutral banner under the card when a whole quality has no buyable colour. */
  qualityBanner(view, slot) {
    if (!KitState.qualityUnavailable(this.recipe, this.catalog, view.key)) return '';
    const entry = this.catalog[view.key] || {};
    const handle = entry.handle || slot.handle || '';
    const href = handle ? joinPath(this.routes.root, `/products/${handle}`) : null;

    return (
      `<div class="kit-banner" role="status">` +
        `<span class="kit-banner__lead">${esc(COPY.qualityGoneLead(view.label))}</span>` +
        `${esc(COPY.qualityGoneRest)}` +
        (href ? `<a class="kit-banner__link" href="${esc(href)}">${esc(COPY.qualityGoneLink)}</a>` : '') +
      `</div>`
    );
  }

  /* --- price panel, stock line, buttons --------------------------------- */

  renderSummary(views, atc) {
    const complete = this.state.size !== null && views.every((v) => v.chosenVariant);
    const signature = JSON.stringify([
      this.state.size, views.map((v) => v.chosen), this.catalogRev,
      atc.enabled, atc.reason, this.ui.busy, this.ui.serverError, complete,
      this._patternOptedOut
    ]);
    if (this.sig.summary === signature) return;
    this.sig.summary = signature;

    const patternOpts = { patternOptedOut: this._patternOptedOut };
    const total = KitState.total(this.state, this.recipe, this.catalog, patternOpts);
    const anyShort = views.some((v) => v.status === 'insufficient')
      || views.some((v) => KitState.qualityUnavailable(this.recipe, this.catalog, v.key));

    /* Optional recipe opt-out checkbox (shown when pattern.optional is true) */
    const pattern = this.recipe && this.recipe.pattern;
    const optOutHtml = pattern && pattern.optional
      ? `<label class="kit-pattern-opt">` +
          `<input type="checkbox" data-kit-action="pattern-opt"${this._patternOptedOut ? '' : ' checked'}> ` +
          esc(COPY.patternOptOut) +
        `</label>`
      : '';

    /* Price panel */
    let price = `<div class="kit-price">`;
    if (complete) {
      const lines = KitState.lines(this.state, this.recipe, this.catalog, patternOpts).map((line) => {
        const left = line.key === 'pattern'
          ? COPY.patternLine(line.label)
          : COPY.lineLabel(line.label, line.colourName, line.qty, KitState.formatDKK(line.unitPrice));
        const right = line.key === 'pattern' && line.lineTotal === 0
          ? COPY.patternFree
          : KitState.formatDKK(line.lineTotal);
        return (
          `<div class="kit-price__line">` +
            `<span class="kit-price__line-label">${esc(left)}</span>` +
            `<span class="kit-price__line-value">${esc(right)}</span>` +
          `</div>`
        );
      }).join('');

      price +=
        `<div class="kit-price__lines">${lines}</div>` +
        `<div class="kit-price__total-row">` +
          `<span class="kit-price__total-label">${esc(COPY.totalLabel)}</span>` +
          `<span class="kit-price__total-value">${esc(KitState.formatDKK(total))}</span>` +
        `</div>`;
    } else {
      price += `<div class="kit-price__empty">${esc(COPY.priceEmpty)}</div>`;
    }
    price += `</div>`;

    /* Stock line */
    const stock =
      `<div class="kit-stock">` +
        `<span class="kit-stock__dot${anyShort ? ' is-critical' : ''}" aria-hidden="true"></span>` +
        `<span>${esc(anyShort ? COPY.stockShort : COPY.stockOk)}</span>` +
      `</div>`;

    /* Buttons. aria-disabled rather than `disabled`, so a keyboard shopper can
       still reach the button and hear the reason bound to it. */
    const blocked = !atc.enabled || this.ui.busy;
    const reasonId = `${this.uid}-reason`;
    const buttons =
      `<button type="button" class="btn btn--full add-to-cart btn--secondary kit-atc"` +
      ` data-kit-action="add" data-fk="atc" aria-disabled="${blocked ? 'true' : 'false'}"` +
      `${this.ui.busy ? ' aria-busy="true"' : ''}` +
      `${atc.reason ? ` aria-describedby="${reasonId}"` : ''}>${esc(COPY.atc)}</button>` +
      (atc.reason
        ? `<p class="kit-reason${atc.reasonKind === 'insufficient' ? ' kit-reason--critical' : ''}" id="${reasonId}">` +
            `<span class="kit-reason__arrow" aria-hidden="true">→</span>` +
            `<span class="kit-reason__text">${esc(atc.reason)}</span>` +
          `</p>`
        : '') +
      `<button type="button" class="kit-buynow" data-kit-action="buy" data-fk="buynow"` +
      ` aria-disabled="${blocked ? 'true' : 'false'}">${esc(COPY.buyNow)}</button>` +
      (this.ui.serverError
        ? `<div class="kit-banner kit-banner--critical" role="alert" data-kit-region="error">` +
            `${esc(this.ui.serverError)}</div>`
        : '');

    this.nodes.summary.innerHTML = optOutHtml + price + stock + buttons;
  }

  /* ---------------------------------------------------------------------- *
   * Focus and scroll continuity across a re-render
   * ---------------------------------------------------------------------- */

  captureFocus() {
    const active = document.activeElement;
    if (!active || !this.root.contains(active)) return null;
    return active.getAttribute('data-fk');
  }

  /** Restores focus by key, then by the closest sensible neighbour. */
  restoreFocus(key) {
    if (!key) return;
    const active = document.activeElement;
    if (active && this.root.contains(active) && active.getAttribute('data-fk') === key) return;
    if (active && active !== document.body && this.root.contains(active)) return;

    const candidates = [key].concat(this.focusFallbacks(key));
    for (const candidate of candidates) {
      const el = this.mount.querySelector(`[data-fk="${candidate.replace(/"/g, '\\"')}"]`);
      if (el && !el.disabled) {
        el.focus();
        return;
      }
    }
  }

  /** Where focus should land when the element it was on no longer exists. */
  focusFallbacks(key) {
    const parts = String(key).split(':');
    if (parts[0] === 'swatch') return [`chosen:${parts[1]}`, `toggle:${parts[1]}`, 'atc'];
    if (parts[0] === 'toggle') return [`chosen:${parts[1]}`, 'atc'];
    if (parts[0] === 'chosen') return [`toggle:${parts[1]}`, 'atc'];
    return ['atc'];
  }

  captureScroll() {
    const map = {};
    this.mount.querySelectorAll('[data-kit-palette]').forEach((list) => {
      map[list.getAttribute('data-kit-palette')] = list.scrollTop;
    });
    return map;
  }

  restoreScroll(map) {
    Object.keys(map).forEach((key) => {
      const list = this.mount.querySelector(`[data-kit-palette="${key.replace(/"/g, '\\"')}"]`);
      if (list && map[key]) list.scrollTop = map[key];
    });
  }

  /** One polite announcement per meaningful change: the total, or why not. */
  announce(atc) {
    if (!this.nodes.live) return;
    if (!this.announced) {
      this.announced = true;
      return; // never announce the first paint
    }

    let message;
    if (this.ui.serverError) message = this.ui.serverError;
    else if (this.ui.notice) message = this.ui.notice;
    else if (atc.enabled) message = COPY.srTotal(KitState.formatDKK(KitState.total(this.state, this.recipe, this.catalog)));
    else message = atc.reason || '';

    if (message === this.lastAnnouncement) return;
    this.lastAnnouncement = message;
    this.nodes.live.textContent = message;
  }

  /* ---------------------------------------------------------------------- *
   * Interaction
   * ---------------------------------------------------------------------- */

  handlePointerdown(event) {
    this.root.classList.remove('is-keyboard');
    // A colour picked with the pointer collapses the slot; one picked with the
    // arrow keys must not, or the shopper loses the palette mid-navigation.
    this.ui.pointerSelect = !!(event.target.closest && event.target.closest('.kit-swatch'));
  }

  handleKeydown(event) {
    this.root.classList.add('is-keyboard');
    this.ui.pointerSelect = false;

    const size = event.target.closest && event.target.closest('[data-kit-action="size"]');
    if (size) this.sizeKeydown(event, size);
  }

  /** Arrow/Home/End navigation for the size radiogroup (buttons, role=radio). */
  sizeKeydown(event, button) {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (keys.indexOf(event.key) === -1) return;

    const buttons = Array.from(this.mount.querySelectorAll('[data-kit-action="size"]'));
    const index = buttons.indexOf(button);
    if (index === -1) return;

    event.preventDefault();
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = buttons.length - 1;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % buttons.length;
    else next = (index - 1 + buttons.length) % buttons.length;

    // Radiogroup semantics: moving the focus also makes the selection. The
    // re-render replaces the buttons, so focus is placed on the new node after it.
    const code = buttons[next].getAttribute('data-kit-value');
    this.selectSize(code);
    const moved = this.mount.querySelector(`[data-fk="size:${code.replace(/"/g, '\\"')}"]`);
    if (moved) moved.focus();
  }

  handleClick(event) {
    const target = event.target.closest && event.target.closest('[data-kit-action]');
    if (!target || !this.root.contains(target)) return;
    const action = target.getAttribute('data-kit-action');

    if (action === 'size') {
      event.preventDefault();
      this.selectSize(target.getAttribute('data-kit-value'));
    } else if (action === 'expand') {
      event.preventDefault();
      this.expandSlot(target.getAttribute('data-kit-slot-key'));
    } else if (action === 'collapse') {
      event.preventDefault();
      this.collapseSlot(target.getAttribute('data-kit-slot-key'));
    } else if (action === 'add') {
      event.preventDefault();
      if (target.getAttribute('aria-disabled') === 'true') return;
      this.addToCart({ checkout: false });
    } else if (action === 'buy') {
      event.preventDefault();
      if (target.getAttribute('aria-disabled') === 'true') return;
      this.addToCart({ checkout: true });
    }
  }

  handleChange(event) {
    const input = event.target;
    if (!input) return;
    if (input.getAttribute('data-kit-action') === 'pattern-opt') {
      this._patternOptedOut = !input.checked;
      this.sig.summary = null; // invalidate so renderSummary re-runs
      this.render();
      return;
    }
    if (input.getAttribute('data-kit-action') !== 'colour') return;
    if (input.disabled) return; // sold-out radios are inert
    this.selectColour(input.getAttribute('data-kit-slot-key'), Number(input.value) || input.value);
  }

  /* --- state transitions ------------------------------------------------ */

  selectSize(code) {
    if (code === null || code === undefined) return;
    if (this.state.size === code) return;

    const previous = this.state.size;
    this.state.size = code;
    // Colour choices survive a size change, always. When one no longer fits we
    // explain it instead of undoing it.
    this.ui.notice = KitState.sizeChangeNotice(previous, code, this.state, this.recipe, this.catalog);
    this.ui.suppressAuto = false;
    this.ui.serverError = null;

    this.syncUrl();
    this.render();
  }

  selectColour(slotKey, variantId) {
    if (!slotKey || !(slotKey in this.state.colors)) return;
    this.state.colors[slotKey] = variantId;
    // Pointer selection collapses the slot (single-open accordion). Keyboard
    // selection keeps it open so arrow keys can keep moving through the palette.
    if (this.ui.pointerSelect) {
      this.state.expanded = null;
      this.ui.suppressAuto = false;
    } else {
      this.state.expanded = slotKey;
    }
    this.ui.pointerSelect = false;
    this.ui.serverError = null;

    this.syncUrl();
    this.render();
  }

  expandSlot(slotKey) {
    if (!slotKey) return;
    this.state.expanded = slotKey;
    this.ui.suppressAuto = false;
    this.render();
  }

  collapseSlot(slotKey) {
    if (!slotKey) return;
    this.state.expanded = null;
    // The slot may have been open because it was the first one missing a colour.
    // Remember the shopper closed it, or it would spring straight back open.
    this.ui.suppressAuto = true;
    this.render();
  }

  /* ---------------------------------------------------------------------- *
   * Deep links — ?size=L&saga=105&woolcotton=3564
   * ---------------------------------------------------------------------- */

  /** Reads the URL into state. Unknown sizes and vanished colours are ignored. */
  restoreFromUrl() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (err) {
      return;
    }

    const size = params.get('size');
    if (size && this.sizes().some((s) => s && s.code === size)) this.state.size = size;

    this.slots().forEach((slot) => {
      const code = params.get(slot.key);
      if (!code) return;
      const entry = this.catalog[slot.key];
      if (!entry) return;
      // Colours travel by code (105), not by variant id — ids differ per shop.
      const match = KitState.annotateVariants(entry.variants, null)
        .find((v) => v.code === String(code).trim());
      if (match) this.state.colors[slot.key] = match.id;
    });
  }

  /** Writes state back to the URL without touching history or other params. */
  syncUrl() {
    if (!window.history || !window.history.replaceState) return;
    let url;
    try {
      url = new URL(window.location.href);
    } catch (err) {
      return;
    }

    if (this.state.size) url.searchParams.set('size', this.state.size);
    else url.searchParams.delete('size');

    this.slots().forEach((slot) => {
      const id = this.state.colors[slot.key];
      const entry = this.catalog[slot.key] || { variants: [] };
      const match = id
        ? KitState.annotateVariants(entry.variants, null).find((v) => v.id === id)
        : null;
      if (match && match.code) url.searchParams.set(slot.key, match.code);
      else url.searchParams.delete(slot.key);
    });

    try {
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (err) {
      /* a blocked replaceState must never break the configurator */
    }
  }

  /* ---------------------------------------------------------------------- *
   * Availability refresh — freshness over the Liquid render
   * ---------------------------------------------------------------------- */

  async refreshAvailability() {
    const jobs = this.slots().map((slot) => {
      const entry = this.catalog[slot.key];
      const handle = (entry && entry.handle) || slot.handle;
      if (!handle) return Promise.resolve(null);
      return this.fetchProduct(handle)
        .then((product) => ({ key: slot.key, product }))
        .catch(() => null);
    });

    const results = await Promise.all(jobs);
    let changed = false;
    results.forEach((result) => {
      if (result && result.product && this.mergeProduct(result.key, result.product)) changed = true;
    });

    if (!changed) return;
    this.catalogRev += 1;
    this.render();
  }

  /** sessionStorage-cached `/products/{handle}.js`. Resolves null on any failure. */
  async fetchProduct(handle) {
    const key = CACHE_PREFIX + handle;
    const cached = store.get(key);
    if (cached && cached.t && (Date.now() - cached.t) < CACHE_TTL_MS && cached.product) {
      return cached.product;
    }

    const url = joinPath(this.routes.root, `/products/${encodeURIComponent(handle)}.js`);
    const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`products.js ${response.status}`);
    const json = await response.json();
    if (!json || !Array.isArray(json.variants)) throw new Error('products.js shape');

    // Store only what the configurator reads, so the cache stays small.
    const product = {
      price: Number.isFinite(json.price) ? json.price : null,
      featured_image: json.featured_image || null,
      variants: json.variants.map((v) => ({
        id: v.id,
        title: v.title,
        option1: v.option1,
        price: v.price,
        available: v.available,
        featured_image: v.featured_image && v.featured_image.src ? { src: v.featured_image.src } : null
      }))
    };
    store.set(key, { t: Date.now(), product });
    return product;
  }

  /**
   * Merges fresh availability over the Liquid data.
   *
   * `available`, price and imagery come from the live endpoint; the inventory
   * COUNT stays whatever Liquid rendered, because /products/{handle}.js does not
   * expose `inventory_quantity`. Variants the endpoint no longer lists have been
   * deleted or unpublished and are dropped; variants it lists that Liquid did not
   * are added with an unknown count, which degrades to availability only.
   *
   * @returns {boolean} whether anything actually changed
   */
  mergeProduct(slotKey, product) {
    const entry = this.catalog[slotKey];
    if (!entry || !product || !Array.isArray(product.variants) || !product.variants.length) return false;

    const before = JSON.stringify(entry.variants.map((v) => [v.id, v.available, v.price]));
    const previous = new Map(entry.variants.map((v) => [v.id, v]));

    entry.variants = product.variants.map((fresh) => {
      const old = previous.get(fresh.id) || {};
      return Object.assign({}, old, {
        id: fresh.id,
        title: fresh.title != null ? fresh.title : old.title,
        option1: fresh.option1 != null ? fresh.option1 : old.option1,
        price: Number.isFinite(fresh.price) ? fresh.price : old.price,
        available: fresh.available,
        featured_image: fresh.featured_image || old.featured_image || null
        // inventory_quantity / inventory_management deliberately preserved.
      });
    });

    if (Number.isFinite(product.price)) entry.price = product.price;
    if (product.featured_image) entry.featured_image = product.featured_image;

    // A colour that vanished entirely cannot stay selected — but that is the
    // variant disappearing, not us clearing a valid choice.
    if (this.state.colors[slotKey] && !entry.variants.some((v) => v.id === this.state.colors[slotKey])) {
      this.state.colors[slotKey] = null;
    }

    return before !== JSON.stringify(entry.variants.map((v) => [v.id, v.available, v.price]));
  }

  handleMediaChange(event) {
    const matches = event && typeof event.matches === 'boolean' ? event.matches : this.mobile.matches;
    if (matches === this.isMobile) return;
    this.isMobile = matches;
    this.render();
  }

  /* ---------------------------------------------------------------------- *
   * Add to cart — one request, one kit group
   * ---------------------------------------------------------------------- */

  /** Returns {[lineKey]: newQty} when an identical kit already exists in the
   *  cart (same handle + size + same set of variant IDs), null otherwise. */
  findMatchingGroup(cart, items) {
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return null;
    if (!this.kitHandle || !this.state.size) return null;

    const groups = new Map();
    for (const line of cart.items) {
      const props = line.properties || {};
      if (props._kit_ref !== this.kitHandle) continue;
      if (props._kit_size !== this.state.size) continue;
      if (!props._kit_id) continue;
      if (!groups.has(props._kit_id)) groups.set(props._kit_id, []);
      groups.get(props._kit_id).push(line);
    }

    const newByVariant = new Map(items.map((i) => [i.id, i.quantity]));

    for (const [, lines] of groups) {
      const existingByVariant = new Map(lines.map((l) => [l.variant_id, l]));
      if (existingByVariant.size !== newByVariant.size) continue;

      let match = true;
      for (const variantId of newByVariant.keys()) {
        if (!existingByVariant.has(variantId)) { match = false; break; }
      }
      if (!match) continue;

      const updates = {};
      for (const [variantId, addQty] of newByVariant) {
        const existing = existingByVariant.get(variantId);
        if (existing && existing.key) updates[existing.key] = existing.quantity + addQty;
      }
      return updates;
    }
    return null;
  }

  async addToCart({ checkout }) {
    if (this.ui.busy) return;

    const items = KitState.cartPayload(this.state, this.recipe, this.catalog, {
      kitId: uuid(),
      kitHandle: this.kitHandle,
      kitTitle: this.kitTitle
    }, { patternOptedOut: this._patternOptedOut });
    if (!items.length) return;

    this.ui.busy = true;
    this.ui.serverError = null;
    this.render();

    try {
      const kitCart = window.KitCart;
      const kitSections = kitCart && kitCart.sectionsToRefresh ? kitCart.sectionsToRefresh() : [];

      // Check whether an identical kit group (same handle + size + variants) is
      // already in the cart — if so, increment its quantities instead of adding
      // a second independent group.
      let existingUpdates = null;
      try {
        const cartResp = await fetch(this.routes.cartJs, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (cartResp.ok) existingUpdates = this.findMatchingGroup(await cartResp.json(), items);
      } catch (_) { /* non-fatal — fall through to normal add */ }

      let payload;

      if (existingUpdates && Object.keys(existingUpdates).length) {
        // Increment existing group
        const updateBody = { updates: existingUpdates };
        if (kitSections.length) updateBody.sections = kitSections.join(',');
        const updateResp = await fetch(this.routes.cartUpdate, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(updateBody)
        });
        if (!updateResp.ok) {
          const errPayload = await updateResp.json().catch(() => null);
          this.ui.serverError =
            (errPayload && (errPayload.description || errPayload.message)) ||
            (window.theme && window.theme.strings && window.theme.strings.cartError) ||
            COPY.cartError;
          return;
        }
        payload = await updateResp.json().catch(() => null);
      } else {
        // Normal add: new kit group
        const addBody = kitSections.length ? { items, sections: kitSections.join(',') } : { items };
        const response = await fetch(this.routes.cartAdd, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(addBody)
        });
        const addPayload = await response.json().catch(() => null);
        if (!response.ok) {
          this.ui.serverError =
            (addPayload && (addPayload.description || addPayload.message)) ||
            (window.theme && window.theme.strings && window.theme.strings.cartError) ||
            COPY.cartError;
          return;
        }
        payload = addPayload;
      }

      // Update the kit groups drawer from fresh section HTML
      const freshSections = payload && payload.sections;
      if (freshSections) {
        const liveWrapper = document.getElementById('KitCartGroupsDrawer');
        if (liveWrapper) {
          for (const sId of Object.keys(freshSections)) {
            const sHtml = freshSections[sId];
            if (!sHtml) continue;
            let freshWrapper = null;
            try {
              freshWrapper = new DOMParser()
                .parseFromString(sHtml, 'text/html')
                .getElementById('KitCartGroupsDrawer');
            } catch (_) {}
            if (freshWrapper) {
              liveWrapper.innerHTML = freshWrapper.innerHTML;
              if (kitCart && kitCart.syncQuantityLocks) kitCart.syncQuantityLocks();
              break;
            }
          }
        }
      }

      if (checkout) {
        window.location.href = this.routes.checkout;
        return;
      }
      await this.afterAdd();
    } catch (err) {
      this.ui.serverError =
        (window.theme && window.theme.strings && window.theme.strings.cartError) || COPY.cartError;
    } finally {
      this.ui.busy = false;
      this.render();
    }
  }

  /**
   * Tells the theme its cart changed. Archetype/Impulse listens for
   * `cart:refresh` (and opens the drawer from `detail.openCart`); the other
   * events are cheap insurance for themes that use different names. When the
   * theme has no drawer we navigate to the cart page instead.
   */
  async afterAdd() {
    let cart = null;
    try {
      const response = await fetch(this.routes.cartJs, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (response.ok) cart = await response.json();
    } catch (err) {
      cart = null;
    }

    // Archetype/Impulse exposes 'drawer' | 'dropdown' | 'page'. Anything that
    // stays on the page keeps us here; 'page' — and a theme we cannot read at
    // all — navigates, so the shopper always gets confirmation of the add.
    const cartType = window.theme && window.theme.settings && window.theme.settings.cartType;
    const drawer = cartType ? cartType !== 'page' : false;

    const fire = (name, detail) => {
      document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
    };
    fire('cart:refresh', { openCart: drawer, cart });
    fire('cart:build', { cart });
    fire('cart:updated', { cart });
    if (drawer) fire('cart:open', { cart });

    if (!drawer) window.location.href = this.routes.cart;
  }
}

/* ══════════════════════════════════════════════════════════════════════════ *
 * Boot
 * ══════════════════════════════════════════════════════════════════════════ */

/** Every live instance, so a section unload in the theme editor can tear down. */
const instances = new Map();

function readBlob(script) {
  try {
    return JSON.parse(script.textContent || '{}');
  } catch (err) {
    return null;
  }
}

function rootFor(script) {
  if (script.dataset.kitRoot) return document.getElementById(script.dataset.kitRoot);
  const sibling = script.closest('.kit-builder');
  if (sibling) return sibling;
  // The blob is commonly rendered next to, not inside, the configurator.
  const parent = script.parentElement;
  return (parent && parent.querySelector('.kit-builder')) || document.querySelector('.kit-builder');
}

function boot(scope) {
  const container = scope && scope.querySelectorAll ? scope : document;
  const scripts = container.querySelectorAll(
    'script[type="application/json"][id^="KitBuilderData"], script[data-kit-builder-data]'
  );

  scripts.forEach((script) => {
    const root = rootFor(script);
    if (!root || instances.has(root)) return;

    const data = readBlob(script);
    if (!data || !data.recipe || !data.catalog) return;

    const builder = new KitBuilder(root, data);
    instances.set(root, builder);
    builder.init();
  });
}

function teardown(scope) {
  instances.forEach((builder, root) => {
    if (scope && scope.contains && !scope.contains(root)) return;
    builder.destroy();
    instances.delete(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
} else {
  boot(document);
}

// Theme editor: sections are swapped in and out of the DOM at runtime.
document.addEventListener('shopify:section:load', (event) => boot(event.target));
document.addEventListener('shopify:section:unload', (event) => teardown(event.target));

export { KitBuilder, boot };
